<template>
  <div class="data-storage">
    <CRow class="mb-4">
      <CCol>
        <label v-translate translate-context="form_configurator.data_storage.label">Storage Provider</label>
        <CSelect 
          :value="selectedStorage" 
          @change="onStorageChange"
          :options="storageOptions"
          :placeholder="$pgettext('form_configurator.data_storage', 'Select storage provider')"
        />
      </CCol>
    </CRow>

    <!-- Pica Storage Information -->
    <div v-if="selectedStorage === dataStorageOptions.PICA_STORAGE" class="mt-3">
      <PAlert color="primary" class="mb-3">
        <h6 class="mb-1" v-translate translate-context="form_configurator.data_storage">Pica Storage</h6>
        <p v-translate translate-context="form_configurator.data_storage">
          Form data will be securely stored in Pica's cloud infrastructure. 
          All data is encrypted at rest and in transit, ensuring maximum security and privacy compliance. 
          Pica will handle all data processing, storage, and retention policies according to our terms of service.
        </p>
        <small class="text-muted" v-translate translate-context="form_configurator.data_storage">
          Data stored with Pica Storage is automatically backed up and maintained for reliability.
        </small>
      </PAlert>
    </div>

    <!-- JSON Endpoint Configuration -->
    <div v-if="selectedStorage === dataStorageOptions.JSON_ENDPOINT" class="mt-3">
      <PAlert color="warning" class="mb-3">
        <h6 class="mb-1" v-translate translate-context="form_configurator.data_storage">JSON Endpoint Integration</h6>
        <p v-translate translate-context="form_configurator.data_storage">
          Form data will be sent to your specified endpoint as JSON. 
          Configure the endpoint URL and JSON pattern below.
        </p>
      </PAlert>
      
      <CRow class="mb-3">
        <CCol>
          <label v-translate translate-context="form_configurator.data_storage.json_endpoint">Endpoint URL</label>
          <CInput 
            v-model="jsonConfig.endpoint" 
            @input="save"
            :placeholder="$pgettext('form_configurator.data_storage.json_endpoint', 'https://api.example.com/webhook')"
          />
          <small class="text-muted" v-translate translate-context="form_configurator.data_storage.json_endpoint">
            The URL where form data will be sent via POST request
          </small>
        </CCol>
      </CRow>
      
      <CRow class="mb-3">
        <CCol>
          <div class="d-flex justify-content-between align-items-center mb-1">
            <label class="mb-0" v-translate translate-context="form_configurator.data_storage.json_endpoint">JSON Pattern</label>
            <CButton
              color="info"
              size="sm"
              variant="ghost"
              @click="showFieldsHelp = true"
            >
              <CIcon name="cil-info" class="mr-1" />
              <span v-translate translate-context="form_configurator.data_storage.json_endpoint">Available Fields</span>
            </CButton>
          </div>
          <CTextarea
            v-model="jsonConfig.pattern"
            @input="onPatternInput"
            :placeholder="jsonPatternPlaceholder"
            rows="6"
          />

          <!-- Fields Help Modal -->
          <CModal
            :show.sync="showFieldsHelp"
            size="lg"
            :title="$pgettext('form_configurator.data_storage.json_endpoint', 'JSON Pattern - Available Fields')"
          >
            <div class="fields-help-content">
              <p v-translate translate-context="form_configurator.data_storage.json_endpoint">
                The JSON pattern defines the structure of data sent to your endpoint.
                Use placeholders in the format <code v-pre>{{field_name}}</code> to include form data.
              </p>

              <div class="help-section">
                <h6 v-translate translate-context="form_configurator.data_storage.json_endpoint">How it works</h6>
                <p class="text-muted small" v-translate translate-context="form_configurator.data_storage.json_endpoint">
                  When a user submits the form, each placeholder is replaced with the actual value.
                  The resulting JSON is sent via POST to your endpoint.
                </p>
              </div>

              <div class="help-section">
                <h6 v-translate translate-context="form_configurator.data_storage.json_endpoint">Form Fields</h6>
                <p class="text-muted small" v-translate translate-context="form_configurator.data_storage.json_endpoint">
                  These are the fields configured in your form. Each field captures user input.
                </p>
                <div v-if="formFields && formFields.length > 0" class="fields-list">
                  <div v-for="field in formFields" :key="field.key" class="field-item">
                    <code v-text="formatPlaceholder(field.key)"></code>
                    <span class="field-type badge badge-secondary ml-2">{{ field.type }}</span>
                    <span v-if="field.type === 'checkbox'" class="text-muted small ml-2">(returns: true/false)</span>
                    <span v-else-if="field.type === 'select'" class="text-muted small ml-2">(returns: selected option value)</span>
                    <span v-else-if="field.type === 'multySelect'" class="text-muted small ml-2">(returns: array of selected values)</span>
                    <span v-else-if="field.type === 'date'" class="text-muted small ml-2">(returns: YYYY-MM-DD, supports modifiers)</span>
                  </div>
                </div>
                <p v-else class="text-muted small" v-translate translate-context="form_configurator.data_storage.json_endpoint">
                  No fields configured yet. Add fields to the form to see them here.
                </p>
              </div>

              <div class="help-section">
                <h6 v-translate translate-context="form_configurator.data_storage.json_endpoint">Consent Fields</h6>
                <p class="text-muted small" v-translate translate-context="form_configurator.data_storage.json_endpoint">
                  Privacy and marketing consent flags. Returns true if accepted, false otherwise.
                </p>
                <div v-if="formConsents && formConsents.length > 0" class="fields-list">
                  <div v-for="consent in formConsents" :key="consent.key" class="field-item">
                    <code v-text="formatPlaceholder(consent.key)"></code>
                    <span class="field-type badge badge-info ml-2">consent</span>
                    <span class="text-muted small ml-2">(returns: true/false)</span>
                  </div>
                </div>
                <p v-else class="text-muted small" v-translate translate-context="form_configurator.data_storage.json_endpoint">
                  No consents configured yet. Add consents to the form to see them here.
                </p>
              </div>

              <div class="help-section">
                <h6 v-translate translate-context="form_configurator.data_storage.json_endpoint">System Fields</h6>
                <p class="text-muted small" v-translate translate-context="form_configurator.data_storage.json_endpoint">
                  Automatically populated fields with submission metadata.
                </p>
                <div class="fields-list">
                  <div class="field-item">
                    <code v-pre>{{created_at}}</code>
                    <span class="text-muted small ml-2">- submission timestamp (ISO 8601)</span>
                  </div>
                  <div class="field-item">
                    <code v-pre>{{user_id}}</code>
                    <span class="text-muted small ml-2">- user identifier (if passed via URL parameters)</span>
                  </div>
                  <div class="field-item">
                    <code v-pre>{{browser_language}}</code>
                    <span class="text-muted small ml-2">- browser language BCP 47 (e.g., it-IT, en-US)</span>
                  </div>
                  <div class="field-item">
                    <code v-pre>{{browser_language_iso}}</code>
                    <span class="text-muted small ml-2">- language code ISO 639-1 (e.g., it, en)</span>
                  </div>
                </div>
              </div>

              <div class="help-section">
                <h6 v-translate translate-context="form_configurator.data_storage.json_endpoint">Date Modifiers</h6>
                <p class="text-muted small" v-translate translate-context="form_configurator.data_storage.json_endpoint">
                  Apply format modifiers to date fields using the pipe syntax.
                </p>
                <div class="fields-list">
                  <div class="field-item">
                    <code v-pre>{{field|iso}}</code>
                    <span class="text-muted small ml-2">- ISO 8601 format (2024-01-15T10:30:00.000Z)</span>
                  </div>
                  <div class="field-item">
                    <code v-pre>{{field|'YYYY-MM-DD'}}</code>
                    <span class="text-muted small ml-2">- custom format (2024-01-15)</span>
                  </div>
                  <div class="field-item">
                    <code v-pre>{{field|'DD/MM/YYYY'}}</code>
                    <span class="text-muted small ml-2">- european format (15/01/2024)</span>
                  </div>
                  <div class="field-item">
                    <code v-pre>{{field|'YYYY-MM-DD HH:mm:ss'}}</code>
                    <span class="text-muted small ml-2">- with time (2024-01-15 10:30:00)</span>
                  </div>
                </div>
              </div>

              <div class="help-section">
                <h6 v-translate translate-context="form_configurator.data_storage.json_endpoint">Sample Pattern</h6>
                <p class="text-muted small" v-translate translate-context="form_configurator.data_storage.json_endpoint">
                  Copy this sample as a starting point. It includes all your form fields and system fields.
                </p>
                <pre class="sample-pattern bg-light p-3 rounded">{{ getSamplePattern() }}</pre>
              </div>
            </div>

            <template #footer>
              <CButton color="secondary" @click="showFieldsHelp = false" v-translate translate-context="form_configurator.data_storage.json_endpoint">
                Close
              </CButton>
              <CButton color="info" variant="outline" @click="copyFullDocumentation">
                <CIcon name="cil-description" class="mr-1" />
                <span v-translate translate-context="form_configurator.data_storage.json_endpoint">Copy Documentation</span>
              </CButton>
              <CButton color="primary" @click="copySamplePattern">
                <CIcon name="cil-copy" class="mr-1" />
                <span v-translate translate-context="form_configurator.data_storage.json_endpoint">Copy Sample</span>
              </CButton>
            </template>
          </CModal>
        </CCol>
      </CRow>
      
      <CRow class="mb-3">
        <CCol>
          <label v-translate translate-context="form_configurator.data_storage.json_endpoint">
            Preview
            <span v-if="jsonConfig.pattern && isJsonValid" class="text-success ml-2">✓</span>
            <span v-else-if="jsonConfig.pattern && !isJsonValid" class="text-danger ml-2">✗</span>
          </label>
          <CTextarea 
            v-model="jsonPreview" 
            rows="6"
            disabled
            class="preview-textarea"
            :class="{'is-valid': jsonConfig.pattern && isJsonValid, 'is-invalid': jsonConfig.pattern && !isJsonValid}"
          />
          <small v-if="jsonConfig.pattern && !isJsonValid" class="text-danger" v-translate translate-context="form_configurator.data_storage.json_endpoint">
            Invalid JSON format. Please check your pattern.
          </small>
        </CCol>
      </CRow>
      
      <!-- Debug Section -->
      <CRow class="mb-3">
        <CCol>
          <CCard class="bg-light">
            <CCardHeader>
              <h6 class="mb-0" v-translate translate-context="form_configurator.data_storage.json_endpoint">Debug Information</h6>
            </CCardHeader>
            <CCardBody>
              <p class="mb-2" v-translate translate-context="form_configurator.data_storage.json_endpoint">
                Test endpoint URL (for testing integration):
              </p>
              <div class="d-flex align-items-center mb-3">
                <code class="flex-grow-1 p-2 bg-white border rounded">{{ testEndpointUrl }}</code>
                <CButton 
                  color="secondary" 
                  size="sm" 
                  class="ml-2"
                  @click="copyTestUrl"
                  v-translate
                  translate-context="form_configurator.data_storage.json_endpoint"
                >
                  Copy
                </CButton>
              </div>
              
              <div class="mt-3">
                <CButton 
                  color="info" 
                  size="sm"
                  @click="checkLastJsonSent"
                  :disabled="checkingLastJson"
                  v-translate
                  translate-context="form_configurator.data_storage.json_endpoint"
                >
                  Check Last JSON Sent
                </CButton>
              </div>
              
              <div v-if="lastJsonResult" class="mt-3">
                <label v-translate translate-context="form_configurator.data_storage.json_endpoint">Last JSON sent:</label>
                <pre class="bg-white border rounded p-2 mb-0" style="max-height: 200px; overflow-y: auto; color: #000;">{{ lastJsonResult }}</pre>
              </div>
              
              <CAlert v-if="lastJsonError" color="danger" class="mt-3 mb-0">
                {{ lastJsonError }}
              </CAlert>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </div>
  </div>
</template>

<script>
import dataStorageOptions from "@/domain/core/constant/dataStorageOptions";
import {mapActions} from "vuex";
import PAlert from "@/components/PAlert.vue";

export default {
  name: "DataStorage",
  components: {PAlert},
  props: {
    data: {
      type: Object,
      default: () => ({
        provider: dataStorageOptions.default(),
        config: {
          endpoint: '',
          pattern: ''
        }
      })
    },
    formFields: {
      type: Array,
      default: () => []
    },
    formConsents: {
      type: Array,
      default: () => []
    },
    picaServiceId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      selectedStorage: dataStorageOptions.default(),
      jsonConfig: {
        endpoint: '',
        pattern: ''
      },
      jsonPreview: '',
      isJsonValid: true,
      checkingLastJson: false,
      lastJsonResult: '',
      lastJsonError: '',
      updatePreviewTimer: null,
      showFieldsHelp: false
    }
  },
  computed: {
    dataStorageOptions() {
      return dataStorageOptions
    },
    storageOptions() {
      return dataStorageOptions.options().map(option => ({
        label: option.name,
        value: option.id
      }))
    },
    jsonPatternPlaceholder() {
      return this.getSamplePattern()
    },
    samplePattern() {
      return this.getSamplePattern()
    },
    testEndpointUrl() {
      const baseUrl = process.env.VUE_APP_PICA_CORE_API_BASE_URL || ''
      const serviceId = this.picaServiceId || '{pica_service_id}'
      return `${baseUrl}/dashboard/v2/form/${serviceId}/test/json_store/`
    }
  },
  emits: ['update:data'],
  watch: {
    data: function () {
      this.init()
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    ...mapActions('eventWizard', [
      'getTestJsonStored'
    ]),
    formatPlaceholder(fieldKey) {
      return `{{${fieldKey}}}`
    },
    getSamplePattern() {
      const fields = {}
      const consents = {}

      // Add all form fields
      if (this.formFields && this.formFields.length > 0) {
        this.formFields.forEach(field => {
          fields[field.key] = `{{${field.key}}}`
        })
      }

      // Add all consent fields
      if (this.formConsents && this.formConsents.length > 0) {
        this.formConsents.forEach(consent => {
          consents[consent.key] = `{{${consent.key}}}`
        })
      }

      // Add system fields
      const systemFields = {
        user_id: '{{user_id}}',
        browser_language: '{{browser_language}}',
        browser_language_iso: '{{browser_language_iso}}',
        created_at: '{{created_at|iso}}'
      }

      const pattern = {
        ...fields,
        ...consents,
        ...systemFields
      }

      return JSON.stringify(pattern, null, 2)
    },
    copySamplePattern() {
      const sample = this.getSamplePattern()
      navigator.clipboard.writeText(sample).then(() => {
        this.$toasted.success(this.$pgettext('form_configurator.data_storage.json_endpoint', 'Sample copied to clipboard'))
      }).catch(() => {
        this.$toasted.error(this.$pgettext('form_configurator.data_storage.json_endpoint', 'Failed to copy sample'))
      })
    },
    copyFullDocumentation() {
      const doc = this.getFullDocumentation()
      navigator.clipboard.writeText(doc).then(() => {
        this.$toasted.success(this.$pgettext('form_configurator.data_storage.json_endpoint', 'Documentation copied to clipboard'))
      }).catch(() => {
        this.$toasted.error(this.$pgettext('form_configurator.data_storage.json_endpoint', 'Failed to copy documentation'))
      })
    },
    getFullDocumentation() {
      let doc = `# JSON Pattern Configuration

## How it works
The JSON pattern defines the structure of data sent to your endpoint.
Use placeholders in the format {{field_name}} to include form data.
When a user submits the form, each placeholder is replaced with the actual value.
The resulting JSON is sent via POST to your endpoint.

## Form Fields
These are the fields configured in the form. Each field captures user input.
`
      if (this.formFields && this.formFields.length > 0) {
        this.formFields.forEach(field => {
          let typeInfo = ''
          switch (field.type) {
            case 'checkbox':
              typeInfo = '(returns: true/false)'
              break
            case 'select':
              typeInfo = '(returns: selected option value)'
              break
            case 'multySelect':
              typeInfo = '(returns: array of selected values)'
              break
            case 'date':
              typeInfo = '(returns: YYYY-MM-DD, supports date modifiers)'
              break
            case 'email':
              typeInfo = '(returns: email address)'
              break
            case 'phone':
              typeInfo = '(returns: phone number)'
              break
            default:
              typeInfo = '(returns: text value)'
          }
          doc += `- {{${field.key}}} [${field.type}] ${typeInfo}\n`
        })
      } else {
        doc += 'No fields configured yet.\n'
      }

      doc += `
## Consent Fields
Privacy and marketing consent flags. Returns true if accepted, false otherwise.
`
      if (this.formConsents && this.formConsents.length > 0) {
        this.formConsents.forEach(consent => {
          doc += `- {{${consent.key}}} [consent] (returns: true/false)\n`
        })
      } else {
        doc += 'No consents configured yet.\n'
      }

      doc += `
## System Fields
Automatically populated fields with submission metadata.
- {{created_at}} - submission timestamp (ISO 8601 format)
- {{user_id}} - user identifier (if passed via URL parameters)
- {{browser_language}} - browser language BCP 47 tag (e.g., it-IT, en-US)
- {{browser_language_iso}} - language code ISO 639-1 lowercase (e.g., it, en)

## Date Modifiers
Apply format modifiers to date fields using the pipe syntax.
- {{field|iso}} - ISO 8601 format (2024-01-15T10:30:00.000Z)
- {{field|'YYYY-MM-DD'}} - custom format (2024-01-15)
- {{field|'DD/MM/YYYY'}} - european format (15/01/2024)
- {{field|'YYYY-MM-DD HH:mm:ss'}} - with time (2024-01-15 10:30:00)

## Sample Pattern
\`\`\`json
${this.getSamplePattern()}
\`\`\`
`
      return doc
    },
    init() {
      this.selectedStorage = this.data?.provider ?? dataStorageOptions.default()
      this.jsonConfig = this.data?.config ?? {
        endpoint: '',
        pattern: ''
      }
      if (this.selectedStorage === dataStorageOptions.JSON_ENDPOINT) {
        this.updateJsonPreview()
      }
    },
    onStorageChange(event) {
      this.selectedStorage = event.target.value
      this.save()
    },
    onPatternInput() {
      // Debounce the preview update to prevent focus loss
      if (this.updatePreviewTimer) {
        clearTimeout(this.updatePreviewTimer)
      }
      this.updatePreviewTimer = setTimeout(() => {
        this.updateJsonPreview()
      }, 300)
      this.save()
    },
    updateJsonPreview() {
      if (!this.jsonConfig.pattern) {
        this.jsonPreview = ''
        this.isJsonValid = true
        return
      }

      let preview = this.jsonConfig.pattern
      
      // Handle special system fields and date modifiers
      preview = preview.replace(/{{created_at(\|[^}]+)?}}/g, (match, modifier) => {
        const now = new Date()
        if (modifier === '|iso') {
          return now.toISOString()
        } else if (modifier && modifier.startsWith('|')) {
          // Extract format string (remove | and quotes)
          const format = modifier.substring(1).replace(/['"]([^'"]+)['"]/, '$1')
          // Return formatted date - in preview we'll show example formats
          return this.getFormattedDateExample(format, now)
        }
        return now.toISOString() // Default to ISO format
      })

      // Handle user_id system field
      preview = preview.replace(/{{user_id}}/g, 'usr_abc123')

      // Handle browser_language system fields
      preview = preview.replace(/{{browser_language}}/g, 'it-IT')
      preview = preview.replace(/{{browser_language_iso}}/g, 'it')

      // Helper to check if a field is boolean type
      const isBooleanField = (fieldKey) => {
        const consent = this.formConsents?.find(c => c.key === fieldKey)
        if (consent) return true
        const field = this.formFields.find(f => f.key === fieldKey)
        return field?.type === 'checkbox'
      }

      // First pass: handle boolean fields with quotes (convert "{{field}}" to true/false without quotes)
      const booleanRegex = /"{{(\w+)}}"/g
      preview = preview.replace(booleanRegex, (match, fieldKey) => {
        if (['created_at', 'user_id', 'browser_language', 'browser_language_iso'].includes(fieldKey)) return match
        if (isBooleanField(fieldKey)) {
          return 'true'
        }
        return match
      })

      // Second pass: replace remaining {{FIELD_NAME}} with dummy values
      const regex = /{{(\w+)(\|[^}]+)?}}/g
      preview = preview.replace(regex, (match, fieldKey, modifier) => {
        // Skip if already processed (system fields)
        if (['created_at', 'user_id', 'browser_language', 'browser_language_iso'].includes(fieldKey)) return match

        // Skip consent fields (already handled in first pass)
        const consent = this.formConsents?.find(c => c.key === fieldKey)
        if (consent) {
          return match
        }

        const field = this.formFields.find(f => f.key === fieldKey)
        if (field) {
          // Handle date fields with modifiers
          if (field.type === 'date' && modifier) {
            const sampleDate = new Date('2024-01-15')
            if (modifier === '|iso') {
              return sampleDate.toISOString()
            } else if (modifier.startsWith('|')) {
              const format = modifier.substring(1).replace(/['"]([^'"]+)['"]/, '$1')
              return this.getFormattedDateExample(format, sampleDate)
            }
          }

          // Generate dummy value based on field type
          switch (field.type) {
            case 'email':
              return 'user@example.com'
            case 'phone':
              return '+39 123 456 7890'
            case 'number':
              return '12345'
            case 'date':
              return '2024-01-15'
            case 'checkbox':
              return match // Already handled in first pass
            case 'select':
              // Return first option value if available
              if (field.params && field.params.options && field.params.options.length > 0) {
                return field.params.options[0].value || field.params.options[0]
              }
              return 'option1'
            case 'text':
            default:
              return 'Sample Text'
          }
        }
        return match // Keep original if field not found
      })

      this.jsonPreview = preview
      
      // Validate JSON
      try {
        JSON.parse(preview)
        this.isJsonValid = true
      } catch (e) {
        this.isJsonValid = false
      }
    },
    getFormattedDateExample(format, date) {
      // Common moment.js format examples
      const formats = {
        'YYYY-MM-DD': date.toISOString().split('T')[0],
        'DD/MM/YYYY': `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`,
        'MM/DD/YYYY': `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`,
        'YYYY-MM-DD HH:mm:ss': date.toISOString().replace('T', ' ').split('.')[0],
        'DD-MM-YYYY': `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`,
        'MMMM D, YYYY': date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        'D MMMM YYYY': date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).replace(/([A-Za-z]+) (\d+), (\d+)/, '$2 $1 $3'),
        'YYYY': String(date.getFullYear()),
        'MM': String(date.getMonth() + 1).padStart(2, '0'),
        'DD': String(date.getDate()).padStart(2, '0')
      }
      
      // Return formatted example or a placeholder showing the format
      return formats[format] || `[${format}]`
    },
    save() {
      this.$emit('update:data', {
        provider: this.selectedStorage,
        config: this.jsonConfig
      })
    },
    copyTestUrl() {
      navigator.clipboard.writeText(this.testEndpointUrl).then(() => {
        this.$toasted.success(this.$pgettext('form_configurator.data_storage.json_endpoint', 'URL copied to clipboard'))
      }).catch(() => {
        this.$toasted.error(this.$pgettext('form_configurator.data_storage.json_endpoint', 'Failed to copy URL'))
      })
    },
    async checkLastJsonSent() {
      this.checkingLastJson = true
      this.lastJsonResult = ''
      this.lastJsonError = ''
      
      try {
        const data = await this.getTestJsonStored()
        this.lastJsonResult = JSON.stringify(data, null, 2)
      } catch (error) {
        this.lastJsonError = this.$pgettext('form_configurator.data_storage.json_endpoint', 'No JSON data found or error retrieving data')
        console.error('Error checking last JSON:', error)
      } finally {
        this.checkingLastJson = false
      }
    }
  }
}
</script>

<style scoped>
.data-storage {
  /* Additional styling can be added here if needed */
}

.preview-textarea {
  font-family: monospace;
  background-color: #f8f9fa;
}

.preview-textarea.is-valid {
  border-color: #28a745;
}

.preview-textarea.is-invalid {
  border-color: #dc3545;
}

/* Fields Help Modal Styles */
.fields-help-content {
  font-size: 0.9rem;
}

.help-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.help-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.help-section h6 {
  color: #333;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.fields-list {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 0.75rem;
}

.field-item {
  padding: 0.4rem 0;
  border-bottom: 1px solid #e9ecef;
}

.field-item:last-child {
  border-bottom: none;
}

.field-item code {
  background-color: #e9ecef;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-size: 0.85rem;
}

.sample-pattern {
  font-family: monospace;
  font-size: 0.85rem;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 300px;
  overflow-y: auto;
}
</style>